import React, { useCallback, useEffect, useRef, useState } from 'react';
import AgenteIcono from '../components/AgenteIcono';
import { toast } from 'sonner';
import { api, formatDateTime } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Send, Loader2, AlertTriangle, CalendarPlus, Info, PlugZap, Mail, X,
  FileSpreadsheet,
} from 'lucide-react';

/**
 * Tarjeta de propuesta del Agente (2026-07-27) — UNA para todas las acciones.
 *
 * Antes era específica de citas y el propio navegador ejecutaba el alta. Ahora
 * la propuesta trae ya `titulo` y `resumen` escritos por el backend, y quien la
 * ejecuta es el servidor: aquí solo se pulsa Confirmar. Añadir una acción nueva
 * no obliga a tocar este componente.
 */
export function PropuestasDelAgente({ m, onEstado, procesandoIdx }) {
  const lista = Array.isArray(m.propuestas) ? m.propuestas : [];
  if (!lista.length) return null;
  return (
    <>
      {lista.map((p, i) => (
        <PropuestaAccionCard
          key={i}
          p={p}
          onEstado={(estado) => onEstado(m, i, estado)}
          procesando={procesandoIdx === `${m.id}:${i}`}
        />
      ))}
    </>
  );
}

function PropuestaAccionCard({ p, onEstado, procesando }) {
  if (!p) return null;
  const estado = p.estado || 'pendiente';
  const titulo = p.titulo || 'Propuesta del Agente';
  const lineas = Array.isArray(p.resumen) ? p.resumen.filter(Boolean) : [];
  return (
    <div className={`mt-2 border rounded-lg p-3 space-y-1 text-sm ${
      estado === 'confirmada' ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20'
        : estado === 'descartada' ? 'border-border opacity-60'
        : 'border-[#2E6E5E]/50 bg-[#2E6E5E]/5'
    }`} data-testid="agente-cita-propuesta">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#13231E]">
        <CalendarPlus className="w-3.5 h-3.5 text-[#2E6E5E]" /> {titulo}
      </div>
      {lineas.map((l, i) => (
        <div key={i} className="text-xs whitespace-pre-wrap">{l}</div>
      ))}
      {estado === 'pendiente' ? (
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => onEstado('confirmada')} disabled={procesando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="agente-cita-confirmar">
            {procesando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5 mr-1" />}
            Confirmar
          </Button>
          <Button size="sm" variant="outline" onClick={() => onEstado('descartada')} disabled={procesando}
            data-testid="agente-cita-descartar">
            <X className="w-3.5 h-3.5 mr-1" /> Descartar
          </Button>
        </div>
      ) : (
        // «✓» de texto y no el emoji de la casilla verde (administración,
        // 5/10/2026: los emojis no se ven bien). Esta tarjeta sale también
        // en la agenda, en el cuadro del Agente IA que pide las citas.
        <div className="text-xs font-medium">{estado === 'confirmada' ? '✓ Hecho' : 'Descartada'}</div>
      )}
    </div>
  );
}

/** Correo preparado por el Agente: se envía solo cuando dirección lo aprueba.
 *  `etiquetas`: el nombre de cada buzón ({clinica: …}), del `.env` de la
 *  instalación (V2: antes iban escritos aquí). */
export function PropuestaCard({ m, onEnviar, onDescartar, enviando, etiquetas }) {
  const p = m.propuesta;
  if (!p) return null;
  const estado = m.propuesta_estado || 'pendiente';
  return (
    <div className={`mt-2 border rounded-lg p-3 space-y-1.5 text-sm ${
      estado === 'enviada' ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20'
        : estado === 'descartada' ? 'border-border opacity-60'
        : 'border-[#2E6E5E]/50 bg-[#2E6E5E]/5'
    }`} data-testid="agente-propuesta">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#13231E]">
        <Mail className="w-3.5 h-3.5 text-[#2E6E5E]" />
        Correo preparado · desde {etiquetas?.[p.cuenta] || p.cuenta}
      </div>
      <div className="text-xs"><span className="text-muted-foreground">Para:</span> {p.para}</div>
      <div className="text-xs"><span className="text-muted-foreground">Asunto:</span> {p.asunto || '(sin asunto)'}</div>
      <pre className="whitespace-pre-wrap break-words font-sans text-xs bg-white dark:bg-card border border-border rounded p-2 max-h-48 overflow-y-auto">{p.cuerpo}</pre>
      {estado === 'pendiente' ? (
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => onEnviar(m.id)} disabled={enviando}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="agente-propuesta-enviar">
            {enviando ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Enviar
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDescartar(m.id)} disabled={enviando}
            data-testid="agente-propuesta-descartar">
            <X className="w-3.5 h-3.5 mr-1" /> Descartar
          </Button>
        </div>
      ) : (
        <div className="text-xs font-medium">
          {estado === 'enviada' ? '✓ Enviado' : 'Descartada'}
        </div>
      )}
    </div>
  );
}

/**
 * Menú «Agente» (2026-07-25) — el chat de dirección con la IA del correo.
 * El agente vigila los dos buzones, publica aquí sus avisos (con énfasis en
 * citas pedidas por correo y quejas) e informes, y responde instrucciones.
 * Solo administradores.
 */

const AVISO_UI = {
  queja: { icon: AlertTriangle, caja: 'border-red-300 bg-red-50 dark:bg-red-950/30', texto: 'text-red-900 dark:text-red-200' },
  cita: { icon: CalendarPlus, caja: 'border-[#1D4ED8]/40 bg-[#1D4ED8]/10', texto: 'text-[#1D4ED8] dark:text-[#93B4FF]' },
};

// Archivo que ha preparado el Agente (un Excel, de momento). Es solo lectura:
// ya está hecho, no hay nada que confirmar — solo bajarlo.
export function Descargas({ descargas }) {
  if (!descargas?.length) return null;
  return (
    <div className="mt-2 space-y-1.5" data-testid="agente-descargas">
      {descargas.map((d) => (
        <a
          key={d.id}
          href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/agente/descarga/${d.id}`}
          className="inline-flex items-center gap-2 rounded-lg border border-[#2E6E5E]/30 bg-[#F0F6F2] px-3 py-2 text-sm font-medium text-[#13231E] hover:bg-[#E2EFE8] transition-colors"
          data-testid={`agente-descarga-${d.id}`}
        >
          <FileSpreadsheet className="w-4 h-4 text-[#2E6E5E]" />
          {d.nombre}
          {d.filas ? <span className="text-xs text-muted-foreground">({d.filas} filas)</span> : null}
        </a>
      ))}
      <div className="text-[10px] text-muted-foreground">Disponible 24 horas.</div>
    </div>
  );
}

function Mensaje({ m, onEnviar, onDescartar, enviandoId, onCitaEstado, citaProcesandoId, etiquetas }) {
  if (m.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="agente-msg-user">
        <div className="burbuja-usuario mensaje-entra max-w-[85%] rounded-2xl rounded-br-sm text-white px-3.5 py-2 text-sm whitespace-pre-wrap break-words">
          {m.user_name && <div className="text-[10px] opacity-75 mb-0.5">{m.user_name}</div>}
          {m.texto}
        </div>
      </div>
    );
  }
  if (m.tipo === 'aviso') {
    const ui = AVISO_UI[m.categoria] || AVISO_UI.cita;
    const Icono = ui.icon;
    return (
      <div className={`border rounded-xl p-3 flex items-start gap-2.5 ${ui.caja}`} data-testid="agente-aviso">
        <Icono className={`w-5 h-5 shrink-0 mt-0.5 ${ui.texto}`} />
        <div className="min-w-0">
          <div className={`text-sm font-medium whitespace-pre-wrap break-words ${ui.texto}`}>{m.texto}</div>
          <div className="text-[10px] text-muted-foreground mt-1">{formatDateTime(m.created_at)}</div>
        </div>
      </div>
    );
  }
  const esInforme = m.tipo === 'informe';
  return (
    <div className="flex justify-start" data-testid="agente-msg-bot">
      <div className="burbuja-agente mensaje-entra max-w-[85%] rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm whitespace-pre-wrap break-words">
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-0.5">
          {esInforme ? <Info className="w-3 h-3" /> : <AgenteIcono size={18} />}
          {esInforme ? 'Informe del correo' : 'Agente'} · {formatDateTime(m.created_at)}
        </div>
        {m.estado === 'trabajando' ? (
          <span className="inline-flex items-center gap-2 text-muted-foreground italic"
            data-testid="agente-msg-trabajando">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Trabajando…
          </span>
        ) : m.estado === 'error' ? (
          <span className="text-destructive">{m.texto}</span>
        ) : m.texto}
        <PropuestaCard m={m} onEnviar={onEnviar} onDescartar={onDescartar}
          enviando={enviandoId === m.id} etiquetas={etiquetas} />
        <Descargas descargas={m.descargas} />
        <PropuestasDelAgente m={m} onEstado={onCitaEstado}
          procesandoIdx={citaProcesandoId} />
      </div>
    </div>
  );
}

export default function AgentePage() {
  const [estado, setEstado] = useState(null);
  const [mensajes, setMensajes] = useState(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviandoId, setEnviandoId] = useState(null); // propuesta en envío
  const finRef = useRef(null);

  const enviarPropuesta = async (chatId) => {
    setEnviandoId(chatId);
    try {
      await api.post(`/api/agente/enviar/${chatId}`);
      toast.success('Correo enviado por el Agente');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setEnviandoId(null);
    }
  };

  const descartarPropuesta = async (chatId) => {
    try {
      await api.post(`/api/agente/propuesta/${chatId}/descartar`);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo descartar');
    }
  };

  const [citaProcesandoId, setCitaProcesandoId] = useState(null);

  // Confirmar ya NO ejecuta aquí (2026-07-27): lo hace el servidor leyendo la
  // propuesta de la base. Así el navegador no puede cambiar los argumentos
  // entre lo propuesto y lo hecho, y añadir acciones no obliga a tocar esto.
  const estadoCitaPropuesta = async (m, indice, estado) => {
    setCitaProcesandoId(`${m.id}:${indice}`);
    try {
      const r = await api.post(`/api/agente/propuesta-accion/${m.id}/${indice}/${estado}`);
      if (estado === 'confirmada') toast.success(r.data?.mensaje || 'Hecho');
      cargar();
    } catch (e) {
      const detail = e?.response?.status === 409
        ? (e?.response?.data?.detail || 'Ese hueco ya no está libre — pídele otro al Agente')
        : e?.response?.data?.detail || 'No se pudo completar la gestión';
      toast.error(detail);
      cargar();  // la propuesta vuelve a pendiente si fallo: refrescar el estado
    } finally {
      setCitaProcesandoId(null);
    }
  };

  const cargar = useCallback((scroll = false) => {
    api.get('/api/agente/mensajes')
      .then((r) => {
        setMensajes(Array.isArray(r.data) ? r.data : []);
        if (scroll) setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'auto' }), 60);
      })
      .catch(() => setMensajes([]));
  }, []);

  useEffect(() => {
    api.get('/api/agente/estado').then((r) => setEstado(r.data)).catch(() => setEstado({}));
    cargar(true);
  }, [cargar]);

  // Los avisos llegan solos con cada sincronización del correo (cada 60 s):
  // se refresca a ese mismo ritmo si la pestaña está a la vista.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) cargar(); }, 30000);
    return () => clearInterval(id);
  }, [cargar]);

  // Mientras haya una respuesta a medias, se refresca sola cada segundo y
  // medio. En cuanto no queda ninguna, se deja de preguntar.
  const hayTrabajando = (mensajes || []).some((m) => m.estado === 'trabajando');
  useEffect(() => {
    if (!hayTrabajando) return undefined;
    const t = setInterval(() => cargar(true), 1500);
    return () => clearInterval(t);
  }, [hayTrabajando, cargar]);

  const enviar = async () => {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    setTexto('');
    // La pregunta se pinta al momento; la respuesta, cuando llegue.
    setMensajes((prev) => [...(prev || []), {
      id: `tmp-${Date.now()}`, role: 'user', tipo: 'chat', texto: t, created_at: new Date().toISOString(),
    }]);
    setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    try {
      // Contesta al momento con la burbuja en «trabajando»: el teclado queda
      // libre y la respuesta se rellena sola cuando el Agente termina.
      await api.post('/api/agente/chat', { texto: t });
      cargar(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'El Agente no pudo responder');
      cargar();
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-4 flex flex-col h-[calc(100dvh-140px)]">
      <PageHeader
        title="Agente"
        subtitle="La IA que vigila el correo de la clínica y el instituto: avisa de citas y quejas, informa y obedece instrucciones."
      />

      {estado && !estado.motor && (
        <div className="cjo-card p-3 flex items-start gap-2 text-sm" data-testid="agente-sin-motor">
          <PlugZap className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <span className="text-muted-foreground">
            <strong className="text-[#13231E]">El Agente no tiene motor de IA.</strong>{' '}
            Falta <code>EMERGENT_LLM_KEY</code> (o <code>GROQ_API_KEY</code>) en el servidor.
          </span>
        </div>
      )}

      <div className="cjo-card flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3" data-testid="agente-chat">
          {mensajes === null ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : mensajes.length === 0 ? (
            <EmptyState
              icon={() => <AgenteIcono size={40} className="mb-3" />}
              title="Todavía no hay nada"
              description="En cuanto lleguen correos, el Agente publicará aquí sus avisos e informes. También puedes escribirle ya: pídele un resumen, un borrador de respuesta, lo que necesites."
            />
          ) : (
            mensajes.map((m) => (
              <Mensaje key={m.id} m={m} etiquetas={estado?.buzones} onEnviar={enviarPropuesta}
                onDescartar={descartarPropuesta} enviandoId={enviandoId}
                onCitaEstado={estadoCitaPropuesta} citaProcesandoId={citaProcesandoId} />
            ))
          )}
          <div ref={finRef} />
        </div>
        <div className="border-t border-border p-2.5 flex items-end gap-2">
          <Textarea
            rows={1}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
            }}
            placeholder="Dile al Agente qué necesitas… (Enter envía)"
            className="flex-1 min-h-[40px] max-h-32 resize-none"
            data-testid="agente-input"
          />
          <Button onClick={enviar} disabled={enviando || !texto.trim()}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F] h-10 w-10 p-0 shrink-0"
            title="Enviar" data-testid="agente-enviar">
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
